/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  Box,
} from '@chakra-ui/react';
import isEqual from 'lodash.isequal';
import { useDispatch, useSelector } from 'react-redux';
import { XClose } from '@kula-ai/icons/outline/XClose';
import { atsJobCreateApiActions } from '@/store/atsJobCreate';
import {
  selectTimezone,
  selectWeekRange,
} from '@/store/atsScheduling/selectors';
import {
  selectInterviewDetails,
  selectCreatedInterviewDetails,
} from '@/store/atsScheduling/selectors';
import {
  fetchInterviewDetails,
  fetchEventId,
  fetchCreatedInterview,
  fetchScheduledInterview,
} from '@/store/atsScheduling/actions';
import { resetInterview } from '@/store/atsScheduling';
import SchedulingCalendar from './SchedulingCalendar';
import SchedulingForm from './SchedulingForm/SchedulingForm';
import { formatEventToForm } from './utils';

export const SchedulingDrawer = ({
  isOpen = false,
  onClose = () => {},
  actionId = undefined,
  stageId,
  jobId,
  candidateId,
  applicationId,
  onSuccess = () => {},
  interviewId = null, // To handle edit interview
  scheduleId = null, // To handle edit schedule
  type = 'manual',
}) => {
  const [event, setEvent] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [scheduledSlots, setScheduledSlots] = useState({});
  const [changedDateRange, setChangedDateRange] = useState(null);
  const [calEvent, setCalEvent] = useState({});
  const [userInfo, setUserInfo] = useState([]);
  const [interviewName, setInterviewName] = useState('');
  const dispatch = useDispatch();
  const weekRange = useSelector(selectWeekRange);
  const interviewDetails = useSelector(selectInterviewDetails);
  const createdInterviewDetails = useSelector(selectCreatedInterviewDetails);
  const selectedTimezone = useSelector(selectTimezone);
  const { schedule_kind, substituted_name } = interviewDetails || {};
  const prevState = useRef({ selectedIds, weekRange, selectedTimezone });

  const { start_time, end_time, time_slots } = createdInterviewDetails || {};
  const variant = type || schedule_kind;
  const isAdhoc = !actionId;
  const [timeSlots, setTimeSlots] = useState(time_slots);
  useEffect(() => {
    if (substituted_name) {
      setInterviewName(substituted_name);
    }
  }, [substituted_name]);

  useEffect(() => {
    setTimeSlots(time_slots);
  }, [time_slots]);

  useEffect(() => {
    if (changedDateRange === 'Selected Option') setTimeSlots([...time_slots]);
  }, [time_slots, changedDateRange]);

  useEffect(() => {
    dispatch(atsJobCreateApiActions('getOffices'));
    if (interviewId) {
      dispatch(fetchCreatedInterview({ interviewId }));
    } else if (scheduleId) {
      dispatch(fetchScheduledInterview({ scheduleId }));
    } else {
      dispatch(
        fetchInterviewDetails({
          id: stageId,
          contactId: candidateId,
          ...(!isAdhoc && { actionId: actionId }),
        })
      );
    }

    return () => {
      dispatch(resetInterview());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetUserInfo = (userInfo) => {
    setUserInfo(userInfo);
  };

  const handleSelectedInterviewers = useCallback((ids) => {
    setSelectedIds(ids);
  }, []);

  const handleFetchedScheduleSlots = useCallback((slotsObj) => {
    setScheduledSlots(slotsObj);
  }, []);

  const changeDateRange = useCallback((value) => {
    setChangedDateRange(value);
  }, []);

  const updateInterviewName = useCallback(
    (value) => setInterviewName(value),
    []
  );

  const fetchInterviewerSlots = () => {
    dispatch(
      fetchEventId({
        from: weekRange?.start,
        to: weekRange?.end,
        time_zone: selectedTimezone,
        user_ids: selectedIds,
        ats_job_id: jobId,
      })
    );
  };

  useEffect(() => {
    if (
      !isEqual(prevState.current, { selectedIds, weekRange, selectedTimezone })
    ) {
      fetchInterviewerSlots();
      prevState.current = { selectedIds, weekRange, selectedTimezone };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, weekRange, selectedTimezone]);

  //update event in cal and form
  useEffect(() => {
    if (start_time && end_time) {
      const constructEvent = formatEventToForm({
        start: start_time,
        end: end_time,
      });
      setCalEvent(constructEvent);
      setEvent({
        ...constructEvent,
        activeStart: constructEvent?.activeStart?.value,
        activeEnd: constructEvent?.activeEnd?.value,
      });
    }
  }, [start_time, end_time]);

  const handleEventChange = useCallback((eventData) => {
    setEvent(eventData);
    setCalEvent({
      ...eventData,
      activeStart: {
        label: eventData?.activeStart,
        value: eventData?.activeStart,
      },
      activeEnd: {
        label: eventData?.activeEnd,
        value: eventData?.activeEnd,
      },
    });
  }, []);

  const handleEventCalChange = (eventData) => {
    setCalEvent(eventData);
    setEvent({
      ...eventData,
      activeStart: eventData?.activeStart?.value,
      activeEnd: eventData?.activeEnd?.value,
      noJump: true,
    });
  };

  const closeModal = useCallback(() => {
    onClose();
  }, []);

  const handelOnSuccess = useCallback(() => {
    onSuccess();
  }, []);

  // const timeSlots =
  //   (scheduledSlots?.scheduleSlots &&
  //     Object.values(scheduledSlots.scheduleSlots).flat()) ||
  //   time_slots;
  return (
    <Drawer isOpen={isOpen} onClose={closeModal}>
      <DrawerOverlay />
      <DrawerContent
        w="calc(100vw - 60px)"
        maxW="calc(100vw - 60px)"
        borderTopLeftRadius="xl"
      >
        <Center
          w="38px"
          h="38px"
          left="-50px"
          top="32px"
          pos="absolute"
          bg="#fff"
          borderRadius="full"
          onClick={closeModal}
        >
          <Icon as={XClose} cursor="pointer" w="20px" h="20px" />
        </Center>
        <DrawerBody p="0" display="flex" maxH="100%" overflow="hidden">
          <Flex flex={1}>
            <Box flex={4} zIndex="99">
              <SchedulingForm
                variant={type || schedule_kind}
                onEventChange={handleEventChange}
                selectedInterviewers={handleSelectedInterviewers}
                handleFetchedScheduleSlots={handleFetchedScheduleSlots}
                event={calEvent}
                actionId={actionId}
                jobId={jobId}
                stageId={stageId}
                candidateId={candidateId}
                applicationId={applicationId}
                interviewId={interviewId}
                scheduleId={scheduleId}
                onSuccess={handelOnSuccess}
                onClose={closeModal}
                userInfo={userInfo}
                changedDateRange={changedDateRange}
                changeDateRange={changeDateRange}
                updateInterviewName={updateInterviewName}
              />
            </Box>
            <Box flex={8} fontSize="12px" zIndex="10">
              <SchedulingCalendar
                onEventChange={handleEventCalChange}
                variant={variant}
                event={event}
                timeSlots={timeSlots}
                interviewerIds={selectedIds}
                jobId={jobId}
                setCbUserInfo={handleSetUserInfo}
                scheduledSlotsObj={scheduledSlots}
                changeDateRange={changeDateRange}
                changedDateRange={changedDateRange}
                interviewName={interviewName}
              />
            </Box>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
